// investVerse.js - Overview Section (matches Hero.js theme)

import React from "react";
import { Container, Box, Typography, Grid, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import InsightsIcon from "@mui/icons-material/Insights";
import { useEffect } from "react";

// Image (ensure this exists)
import InvestVerseGraphic from "../assets/investverse_overview.png";

export default function InvestVerse() {
    useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const theme = useTheme();
  const navigate = useNavigate();

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        color: "white",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={6} alignItems="center">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "3.4rem" },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  Introducing <Box component="span" sx={{ color: PRIMARY_GREEN }}>InvestVerse</Box>
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h6" sx={{ color: LIGHT_GRAY, fontWeight: 300, mb: 3 }}>
                  A next‑gen social investing platform built for modern creators, traders, and long‑term
                  wealth builders.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, mb: 4, fontWeight: 300 }}>
                  Learn, share, and grow financial knowledge inside a trusted investor community. Track
                  insights, follow investor journeys, and discover real discussion — not noise.
                </Typography>
              </motion.div>

              {/* FEATURES */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={4}>
                  <motion.div variants={itemVariants}>
                    <TrendingUpIcon sx={{ fontSize: 45, color: PRIMARY_GREEN }} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Market Insights</Typography>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <motion.div variants={itemVariants}>
                    <GroupsIcon sx={{ fontSize: 45, color: PRIMARY_GREEN }} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Investor Communities</Typography>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <motion.div variants={itemVariants}>
                    <InsightsIcon sx={{ fontSize: 45, color: PRIMARY_GREEN }} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Portfolio Discussions</Typography>
                  </motion.div>
                </Grid>
              </Grid>

              {/* CTAS */}
              <motion.div
                variants={itemVariants}
                style={{ display: "flex", gap: theme.spacing(2) }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/investverse-sneakpeek")}
                >
                  Explore InvestVerse
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/contact")}
                >
                  Partner With Us
                </Button>
              </motion.div>
            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants} style={{ display: "flex", justifyContent: "center" }}>
                <Box
                  component="img"
                  src={InvestVerseGraphic}
                  alt="InvestVerse Overview"
                  sx={{
                    width: "100%",
                    maxWidth: 420,
                    borderRadius: 4,
                    boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    objectFit: "contain",
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}
